import React, { useRef } from "react";
import {
    useListBox,
    type AriaListBoxOptions,
    useOption,
    useListBoxSection,
} from "@react-aria/listbox";
import { type ListState } from "react-stately";
import type { Node } from "@react-types/shared";
import cn from "classnames";

type ListBoxProps<T extends object> = {
    listBoxRef?: React.RefObject<HTMLUListElement>;
    state: ListState<T>;
} & AriaListBoxOptions<T>;

interface SectionProps {
    section: Node<object>;
    state: ListState<object>;
}

type OptionProps = {
    item: Node<object>;
    state: ListState<object>;
};

const ListBox = <T extends object>(props: ListBoxProps<T>) => {
    const ref = useRef<HTMLUListElement>(null);

    const { listBoxRef = ref, state } = props;
    const { listBoxProps } = useListBox(props, state, listBoxRef);

    return (
        <ul {...listBoxProps} ref={listBoxRef}>
            {[...state.collection].map((item) =>
                item.type === "section" ? (
                    <ListBoxSection
                        key={item.key}
                        section={item}
                        state={state}
                    />
                ) : (
                    <Option key={item.key} item={item} state={state} />
                )
            )}
        </ul>
    );
};

const ListBoxSection = ({ section, state }: SectionProps) => {
    const { itemProps, groupProps } = useListBoxSection({
        heading: section.rendered,
        "aria-label": section["aria-label"],
    });

    return (
        <>
            <li {...itemProps} className="list-box__section">
                <ul {...groupProps}>
                    {[...section.childNodes].map((node) => (
                        <Option key={node.key} item={node} state={state} />
                    ))}
                </ul>
            </li>
        </>
    );
};

const Option = ({ item, state }: OptionProps) => {
    const ref = useRef<HTMLLIElement>(null);
    const { optionProps, isFocused } = useOption(
        {
            key: item.key,
        },
        state,
        ref
    );

    return (
        <li
            {...optionProps}
            ref={ref}
            className={cn("list-box__item", {
                "list-box__item--focused": isFocused,
            })}
        >
            {item.rendered}
        </li>
    );
};

export default ListBox;
